import {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert, Button, Card, Checkbox, Empty, Flex, Form, Input, Modal,
    Popconfirm, Statistic, Table, Tabs, Tag, Typography, message
} from "antd";
import {
    DeleteOutlined, DownloadOutlined, EditOutlined, ImportOutlined, PlusOutlined, SearchOutlined
} from '@ant-design/icons';
import Apiurl from "../ConstURL";

const {Title, Text, Paragraph} = Typography;

// как на бэкенде: "48,62", "203.16", "1163", "1 163,00"
const pricePattern = /^\s*\d+(?:[  ]\d{3})*(?:[.,]\d+)?\s*$/;

// "1,163" — то ли 1163, то ли 1.163; бэкенд такую цену отклоняет
const ambiguousPricePattern = /^\s*\d{1,3},\d{3}\s*$/;

// SKU в любом виде (GRG4628705, G4628705, 4628705) хранится как "G" + первая группа цифр
function normalizeSku(raw) {
    const digits = (raw || "").match(/\d+/);
    return digits ? "G" + digits[0] : "";
}

function formatPrice(value) {
    return Number(value).toLocaleString("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

// Go отдаёт «пустое» время как 0001-01-01 — у записей, которые ещё не правили через сайт
function formatDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "—";
    return date.toLocaleString("ru-RU");
}

function formatAuthor(value) {
    if (!value) return "—";
    if (value === "internalToken") return <Tag>служебный токен</Tag>;
    return value;
}

// /admin/prices/* принимают JWT из localStorage; без доступа бэкенд отвечает пустым 404
async function pricesApi(path, params = {}, init = {}) {
    const query = new URLSearchParams({token: localStorage.getItem("token") || "", ...params});
    const response = await fetch(Apiurl + path + "?" + query, init);
    if (response.status === 404) {
        throw new Error("нет доступа: войдите заново (или бэкенд ещё не обновлён)");
    }
    const data = await response.json();
    if (!data.Status) {
        throw new Error(data.Message || "неизвестная ошибка");
    }
    return data;
}

const changeColumns = {
    sku: {title: "SKU", dataIndex: "sku", key: "sku"},
    price: {title: "Цена", dataIndex: "price", key: "price", align: "right", render: formatPrice},
    oldPrice: {title: "Было", dataIndex: "oldPrice", key: "oldPrice", align: "right", render: formatPrice},
    newPrice: {title: "Станет", dataIndex: "price", key: "newPrice", align: "right", render: (value) => <Text strong>{formatPrice(value)}</Text>},
};

export default function Brandprices() {
    const [form] = Form.useForm();
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const [editing, setEditing] = useState(null); // null — модалка закрыта, {} — новая цена, {sku, price} — правка
    const [saving, setSaving] = useState(false);

    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState("");
    const [replaceAll, setReplaceAll] = useState(false);
    const [preview, setPreview] = useState(null);
    const [checking, setChecking] = useState(false);
    const [applying, setApplying] = useState(false);
    // номер последней проверки: ответ на устаревшую проверку не должен вернуть старый предпросмотр
    const checkSeq = useRef(0);

    const skuInput = Form.useWatch("sku", form);

    function fetchPrices() {
        setLoading(true);
        pricesApi("/admin/prices/list")
            .then(data => setPrices(data.Data || []))
            .catch(err => message.error("Не удалось загрузить цены: " + err.message))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchPrices();
    }, []);

    const filtered = useMemo(() => {
        const query = search.trim();
        if (!query) return prices;
        const digits = (query.match(/\d+/) || [""])[0];
        return prices.filter(row => digits ? row.sku.includes(digits) : row.sku.toLowerCase().includes(query.toLowerCase()));
    }, [prices, search]);

    // при добавлении предупреждаем, что такой SKU уже есть и его цена заменится
    const existingForInput = useMemo(() => {
        if (!editing || editing.sku) return null;
        const sku = normalizeSku(skuInput);
        return sku ? prices.find(row => row.sku === sku) || null : null;
    }, [editing, skuInput, prices]);

    const openEditor = (row) => {
        setEditing(row || {});
        form.setFieldsValue(row
            ? {sku: row.sku, price: String(row.price).replace(".", ",")}
            : {sku: "", price: ""});
    };

    const savePrice = (values) => {
        setSaving(true);
        pricesApi("/admin/prices/set", {sku: values.sku.trim(), price: values.price.trim()}, {method: "POST"})
            .then(data => {
                message.success((data.Created ? "Цена добавлена: " : "Цена обновлена: ") + data.Data.sku + " — " + formatPrice(data.Data.price));
                setEditing(null);
                fetchPrices();
            })
            .catch(err => message.error("Цена не сохранена: " + err.message))
            .finally(() => setSaving(false));
    };

    const deletePrice = (sku) => {
        pricesApi("/admin/prices/delete/" + encodeURIComponent(sku), {}, {method: "POST"})
            .then(() => {
                message.success("Цена для " + sku + " удалена");
                fetchPrices();
            })
            .catch(err => message.error("Не удалось удалить: " + err.message));
    };

    const invalidatePreview = () => {
        checkSeq.current++;
        setPreview(null);
    };

    const importRequest = (params, text) => pricesApi("/admin/prices/import", params, {
        method: "POST",
        headers: {"Content-Type": "text/plain;charset=utf-8"},
        body: text,
    });

    const checkImport = () => {
        const seq = ++checkSeq.current;
        const text = importText;
        const replace = replaceAll;
        setChecking(true);
        importRequest(replace ? {dryrun: "true", replace: "true"} : {dryrun: "true"}, text)
            .then(data => {
                // пока шла проверка, текст или режим могли поменять — такой ответ уже не про них
                if (seq === checkSeq.current) setPreview({...data, forText: text, forReplace: replace});
            })
            .catch(err => message.error("Проверка не прошла: " + err.message))
            .finally(() => setChecking(false));
    };

    // применяем ровно то, что проверили; при замене бэкенд сверит число удалений с предпросмотром
    const applyImport = () => {
        const params = preview.forReplace
            ? {replace: "true", expectremoved: String(preview.Removed.length)}
            : {};
        setApplying(true);
        importRequest(params, preview.forText)
            .then(data => {
                message.success(`Готово: добавлено ${data.Inserted}, изменено ${data.Updated}, удалено ${data.Deleted}`, 6);
                closeImport();
                fetchPrices();
            })
            .catch(err => message.error("Импорт не применён: " + err.message))
            .finally(() => setApplying(false));
    };

    const closeImport = () => {
        setImportOpen(false);
        setImportText("");
        setReplaceAll(false);
        invalidatePreview();
    };

    const exportTsv = () => {
        const lines = prices.map(row => row.sku + "\t" + row.price);
        const blob = new Blob([lines.join("\n") + "\n"], {type: "text/tab-separated-values;charset=utf-8"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "brandprices-" + new Date().toISOString().slice(0, 10) + ".tsv";
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const columns = [
        {
            title: "SKU",
            dataIndex: "sku",
            key: "sku",
            sorter: (a, b) => a.sku.localeCompare(b.sku),
            render: (sku) => <Text copyable strong>{sku}</Text>,
        },
        {
            title: "Цена",
            dataIndex: "price",
            key: "price",
            align: "right",
            sorter: (a, b) => a.price - b.price,
            render: formatPrice,
        },
        {
            title: "Изменено",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: formatDate,
        },
        {
            title: "Кем",
            dataIndex: "updatedBy",
            key: "updatedBy",
            render: formatAuthor,
        },
        {
            title: "",
            key: "actions",
            align: "right",
            render: (_, row) => (
                <Flex gap={4} justify="end">
                    <Button type="text" icon={<EditOutlined/>} onClick={() => openEditor(row)}/>
                    <Popconfirm
                        title={"Удалить цену для " + row.sku + "?"}
                        description="SKU снова будет отдаваться с ценой Zoro"
                        onConfirm={() => deletePrice(row.sku)}
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{danger: true}}
                    >
                        <Button danger type="text" icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Flex>
            ),
        },
    ];

    const pendingChanges = preview
        ? preview.Added.length + preview.Changed.length + preview.Removed.length
        : 0;

    const previewTabs = preview ? [
        {
            key: "added",
            label: `Новые (${preview.Added.length})`,
            children: <Table size="small" rowKey="sku" dataSource={preview.Added}
                             columns={[changeColumns.sku, changeColumns.price]} pagination={{pageSize: 10}}/>,
        },
        {
            key: "changed",
            label: `Изменятся (${preview.Changed.length})`,
            children: <Table size="small" rowKey="sku" dataSource={preview.Changed}
                             columns={[changeColumns.sku, changeColumns.oldPrice, changeColumns.newPrice]} pagination={{pageSize: 10}}/>,
        },
        ...(preview.forReplace ? [{
            key: "removed",
            label: <Text type={preview.Removed.length ? "danger" : undefined}>Удалятся ({preview.Removed.length})</Text>,
            children: <Table size="small" rowKey="sku" dataSource={preview.Removed}
                             columns={[changeColumns.sku, changeColumns.price]} pagination={{pageSize: 10}}/>,
        }] : []),
        {
            key: "skipped",
            label: `Пропущены (${preview.SkippedCount})`,
            children: preview.SkippedCount === 0 ? <Empty description="Все строки разобраны"/> : (
                <>
                    <Paragraph type="secondary">
                        В этих строках не нашлось SKU с цифрами или цены — они не попадут в таблицу.
                        {preview.SkippedCount > preview.Skipped.length && ` Показаны первые ${preview.Skipped.length}.`}
                    </Paragraph>
                    <pre className="code-block">{preview.Skipped.join("\n")}</pre>
                </>
            ),
        },
    ] : [];

    return (
        <div>
            <Flex justify="space-between" align="center" style={{marginBottom: 24}}>
                <Title level={3} style={{margin: 0}}>Цены брендов</Title>
                <Text type="secondary">{prices.length} позиций</Text>
            </Flex>

            <Card style={{marginBottom: 16}}>
                <Paragraph type="secondary">
                    Договорные цены с брендами. Если SKU есть в таблице, <Text code>/info/about/sku</Text> и{" "}
                    <Text code>/info/about/sku/quantity</Text> отдают <Text code>Price</Text> = цена из таблицы
                    и <Text code>OriginalPrice</Text> = 0. Остальные SKU отдаются как раньше.
                    SKU можно вводить в любом виде — GRG4628705, G4628705, 4628705: хранится как G4628705.
                </Paragraph>
                <Flex gap={8} wrap="wrap">
                    <Input
                        allowClear
                        prefix={<SearchOutlined style={{color: 'rgba(255,255,255,0.3)'}}/>}
                        placeholder="Поиск по SKU"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{width: 260}}
                    />
                    <Button type="primary" icon={<PlusOutlined/>} onClick={() => openEditor(null)}>
                        Добавить цену
                    </Button>
                    <Button icon={<ImportOutlined/>} onClick={() => setImportOpen(true)}>
                        Импорт таблицы
                    </Button>
                    <Button icon={<DownloadOutlined/>} onClick={exportTsv} disabled={prices.length === 0}>
                        Скачать TSV
                    </Button>
                </Flex>
            </Card>

            <Card>
                <Table
                    rowKey="sku"
                    columns={columns}
                    dataSource={filtered}
                    loading={loading}
                    scroll={{x: true}}
                    pagination={{defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 500]}}
                    locale={{emptyText: <Empty description={search ? "Ничего не найдено" : "Цен пока нет"}/>}}
                />
            </Card>

            <Modal
                open={editing !== null}
                title={editing && editing.sku ? "Цена для " + editing.sku : "Новая цена"}
                onCancel={() => setEditing(null)}
                onOk={() => form.submit()}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={savePrice} autoComplete="off" preserve={false}>
                    <Form.Item
                        name="sku"
                        label="SKU"
                        rules={[
                            {required: true, message: "Введите SKU"},
                            {pattern: /\d/, message: "В SKU должны быть цифры"},
                        ]}
                        extra={existingForInput && (
                            <Text type="warning">
                                Такой SKU уже есть ({existingForInput.sku}, {formatPrice(existingForInput.price)}) — цена будет заменена
                            </Text>
                        )}
                    >
                        <Input placeholder="GRG4628705" disabled={Boolean(editing && editing.sku)}/>
                    </Form.Item>
                    <Form.Item
                        name="price"
                        label="Цена, USD"
                        rules={[
                            {required: true, message: "Введите цену"},
                            {pattern: pricePattern, message: "Цена — число: 48,62 или 48.62"},
                            {
                                validator: (_, value) => value && ambiguousPricePattern.test(value)
                                    ? Promise.reject(new Error("Неоднозначно: пишите 1163 или 1 163,00"))
                                    : Promise.resolve(),
                            },
                        ]}
                    >
                        <Input placeholder="48,62"/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={importOpen}
                title="Импорт таблицы цен"
                width={860}
                onCancel={closeImport}
                maskClosable={false}
                footer={
                    <Flex justify="end" gap={8}>
                        <Button onClick={closeImport}>Отмена</Button>
                        <Button onClick={checkImport} loading={checking} disabled={!importText.trim()}>
                            Проверить
                        </Button>
                        {preview && preview.Removed.length > 0 ? (
                            <Popconfirm
                                title={`Будет удалено ${preview.Removed.length} позиций`}
                                description="SKU, которых нет в списке, снова будут отдаваться с ценой Zoro"
                                onConfirm={applyImport}
                                okText="Применить"
                                cancelText="Отмена"
                                okButtonProps={{danger: true}}
                            >
                                <Button type="primary" danger loading={applying}>Применить</Button>
                            </Popconfirm>
                        ) : (
                            <Button type="primary" onClick={applyImport} loading={applying}
                                    disabled={!preview || pendingChanges === 0}>
                                Применить
                            </Button>
                        )}
                    </Flex>
                }
            >
                <Paragraph type="secondary">
                    Вставьте таблицу из Excel или текст: по строке на позицию, SKU и цена через табуляцию или пробелы.
                    Шапка и строки без SKU («не открывается») пропускаются сами. Сначала нажмите «Проверить» —
                    в базу ничего не запишется, пока вы не нажмёте «Применить».
                </Paragraph>
                <Input.TextArea
                    rows={10}
                    value={importText}
                    onChange={e => {
                        setImportText(e.target.value);
                        invalidatePreview();
                    }}
                    placeholder={"GRG4628705\t48,62\nGRG6414698\t222,95"}
                    style={{fontFamily: "monospace", marginBottom: 12}}
                />
                <Checkbox
                    checked={replaceAll}
                    onChange={e => {
                        setReplaceAll(e.target.checked);
                        invalidatePreview();
                    }}
                >
                    Заменить таблицу целиком — удалить SKU, которых нет в списке
                </Checkbox>

                {preview && (
                    <div style={{marginTop: 16}}>
                        <Flex gap={32} wrap="wrap" style={{marginBottom: 12}}>
                            <Statistic title="Разобрано SKU" value={preview.Parsed}/>
                            <Statistic title="Новые" value={preview.Added.length} valueStyle={{color: "#52c41a"}}/>
                            <Statistic title="Изменятся" value={preview.Changed.length} valueStyle={{color: "#faad14"}}/>
                            <Statistic title="Без изменений" value={preview.Unchanged}/>
                            {preview.forReplace && (
                                <Statistic title="Удалятся" value={preview.Removed.length}
                                           valueStyle={{color: preview.Removed.length ? "#ff4d4f" : undefined}}/>
                            )}
                            <Statistic title="Пропущено строк" value={preview.SkippedCount}/>
                        </Flex>
                        {preview.Duplicates > 0 && (
                            <Alert type="warning" showIcon style={{marginBottom: 12}}
                                   message={`${preview.Duplicates} SKU повторяются в списке — взята цена из последней строки`}/>
                        )}
                        {pendingChanges === 0 && (
                            <Alert type="info" showIcon style={{marginBottom: 12}} message="Изменений нет — таблица уже такая"/>
                        )}
                        <Tabs size="small" items={previewTabs}/>
                    </div>
                )}
            </Modal>
        </div>
    );
}
